import { Injectable } from '@angular/core';
import { TranslateService } from '@shared/translate/translate.service';
import { ConfigurationId, PPSSState } from '@types';
import { ColDef, ColGroupDef } from '@ag-grid-community/core';
import {
  getDefaultIdentifierColumnDefsForIntl,
  getDefaultPerformanceColumnDefsForIntl,
  getDefaultRatingColumnDefsForIntl,
} from '../ag-grid-helpers/ppss-col-defs/col-def-intl-default';
import { getDefaultPerformanceColumnDefsForAU } from '../ag-grid-helpers/ppss-col-defs/col-def-au-default';
import { getPerformanceColumnDefsForUS_529 } from '../ag-grid-helpers/ppss-col-defs/col-def-us-529';
import {
  getPerformanceColumnDefsForUS_CEF,
  getRatingColumnDefsForUS_CEF,
  getYieldColumnDefsForUS_CEF,
} from '../ag-grid-helpers/ppss-col-defs/col-def-us-cef';
import {
  getDefaultPerformanceColumnDefsForUS,
  getDefaultRatingColumnDefsForUS,
  getDefaultYieldColumnDefsForUS,
} from '../ag-grid-helpers/ppss-col-defs/col-def-us-default';
import {
  getPerformanceColumnDefsForUS_ETF,
  getRatingColumnDefsForUS_ETF,
  getYieldColumnDefsForUS_ETF,
} from '../ag-grid-helpers/ppss-col-defs/col-def-us-etf';
import { getYieldColumnDefsForUS_MMF } from '../ag-grid-helpers/ppss-col-defs/col-def-us-mmf';
import { getPerformanceColumnDefsForUS_SMA } from '../ag-grid-helpers/ppss-col-defs/col-def-us-sma';
import { getPerformanceColumnDefsForUS_VIP } from '../ag-grid-helpers/ppss-col-defs/col-def-us-vip';
import { PpssFundidentifiersComponent } from '../ppss/ppss-fundidentifiers/ppss-fundIdentifiers.component';
import { PpssPerformanceComponent } from '../ppss/ppss-performance/ppss-performance.component';
import { PpssRatingsComponent } from '../ppss/ppss-ratings/ppss-ratings.component';
import { PpssYieldsComponent } from '../ppss/ppss-yields/ppss-yields.component';
import { AgGridHelperService } from './ag-grid-helper.service';
import { getDefaultPerformanceColumnDefsForSG } from '../ag-grid-helpers/ppss-col-defs/col-def-sg-default';
import { getPerformanceColumnDefsForAU_ETF } from '../ag-grid-helpers/ppss-col-defs/col-def-au-etf';
import { getDefaultRatingColumnDefsForCZ } from '../ag-grid-helpers/ppss-col-defs/col-def-cz-default';
import { getDefaultRatingColumnDefsForES } from '../ag-grid-helpers/ppss-col-defs/col-def-es-default';
import { getDefaultRatingColumnDefsForPL } from '../ag-grid-helpers/ppss-col-defs/col-def-pl-default';
import { getDefaultPerformanceColumnDefsForBelgium } from '../ag-grid-helpers/ppss-col-defs/col-def-be-default';
import {
  getDefaultIdentifierColumnDefsForCA,
  getDefaultRatingColumnDefsForCA,
  getPerformanceColumnDefsForCA_ETF,
} from '../ag-grid-helpers/ppss-col-defs/col-def-ca-default';
import {
  getDefaultPerformanceColumnDefsForBR,
  getDefaultRatingColumnDefsForBR,
} from '../ag-grid-helpers/ppss-col-defs/col-def-br-default';
import { getDefaultRatingColumnDefsForOffshore } from '../ag-grid-helpers/ppss-col-defs/col-def-offshore-default';
import {
  getPerformanceColumnDefsForUS_NjBest,
  getPriceColumnDefsForUS_NjBest,
} from '../ag-grid-helpers/ppss-col-defs/col-def-us-njbest';
import { PpssPriceComponent } from '../ppss/ppss-price/ppss-price.component';
import { getDefaultPerformanceColumnDefsForZA } from '../ag-grid-helpers/ppss-col-defs/col-def-za-default';
import {
  getDefaultPerformanceColumnDefsForDE,
  getDefaultRatingColumnDefsForDE,
} from '../ag-grid-helpers/ppss-col-defs/col-def-de-default';

export type PPSSTab =
  | PpssPerformanceComponent
  | PpssFundidentifiersComponent
  | PpssYieldsComponent
  | PpssRatingsComponent
  | PpssPriceComponent;

export enum PPSSTabName {
  'PERFORMANCE',
  'IDENTIFIERS',
  'YIELDS',
  'RATINGS',
  'PRICE',
}

@Injectable({
  providedIn: 'root',
})
export class AgGridColFactoryService {
  /**
   * Return column definition based upon geography, tab name & product type selected.
   * @param ppssTabName ppss tab component name
   * @param ppssTab ppss tab component
   * @param state ppss service state
   * @param agGridHelperService ag grid helper service instance
   * @param translateService translate service instance
   */
  generateColDefs(
    ppssTabName: PPSSTabName,
    ppssTab: PPSSTab,
    state: PPSSState,
    agGridHelperService: AgGridHelperService,
    translateService: TranslateService
  ): ColGroupDef[] | ColDef[] {
    // ********** SIMPLE CONFIG: Insert "else-if" in between for any other geographic case. **********
    if (state.siteConfig.country === 'US') {
      switch (ppssTabName) {
        case PPSSTabName.PERFORMANCE: {
          switch ((ppssTab as PpssPerformanceComponent).productType) {
            case ConfigurationId.CEF:
              return getPerformanceColumnDefsForUS_CEF(
                ppssTab as PpssPerformanceComponent,
                state,
                agGridHelperService
              );
            case ConfigurationId.ETF:
              return getPerformanceColumnDefsForUS_ETF(
                ppssTab as PpssPerformanceComponent,
                state,
                agGridHelperService,
                translateService
              );
            case ConfigurationId.VIP:
              return getPerformanceColumnDefsForUS_VIP(
                ppssTab as PpssPerformanceComponent,
                state,
                agGridHelperService,
                translateService
              );
            case ConfigurationId._529:
              if (state.isNjBest) {
                return getPerformanceColumnDefsForUS_NjBest(
                  ppssTab as PpssPerformanceComponent,
                  state,
                  agGridHelperService,
                  translateService
                );
              } else {
                return getPerformanceColumnDefsForUS_529(
                  ppssTab as PpssPerformanceComponent,
                  state,
                  agGridHelperService,
                  translateService
                );
              }

            case ConfigurationId.SMA:
              return getPerformanceColumnDefsForUS_SMA(
                ppssTab as PpssPerformanceComponent,
                state,
                agGridHelperService,
                translateService
              );
            default:
              // This will serve for MF/MMF/TIF/IF.
              return getDefaultPerformanceColumnDefsForUS(
                ppssTab as PpssPerformanceComponent,
                state,
                agGridHelperService,
                translateService
              );
          }
        }
        case PPSSTabName.YIELDS: {
          switch ((ppssTab as PpssYieldsComponent).productType) {
            case ConfigurationId.MMF:
              return getYieldColumnDefsForUS_MMF(
                ppssTab as PpssYieldsComponent,
                agGridHelperService,
                translateService
              );
            case ConfigurationId.CEF:
              return getYieldColumnDefsForUS_CEF(
                ppssTab as PpssYieldsComponent,
                agGridHelperService,
                translateService
              );
            case ConfigurationId.ETF:
              return getYieldColumnDefsForUS_ETF(
                ppssTab as PpssYieldsComponent,
                agGridHelperService,
                translateService
              );
            default:
              return getDefaultYieldColumnDefsForUS(
                ppssTab as PpssYieldsComponent,
                agGridHelperService,
                translateService
              );
          }
        }
        case PPSSTabName.RATINGS: {
          switch ((ppssTab as PpssRatingsComponent).productType) {
            case ConfigurationId.CEF:
              return getRatingColumnDefsForUS_CEF(
                ppssTab as PpssRatingsComponent,
                agGridHelperService
              );
            case ConfigurationId.ETF:
              return getRatingColumnDefsForUS_ETF(
                ppssTab as PpssRatingsComponent,
                agGridHelperService
              );
            default:
              return getDefaultRatingColumnDefsForUS(
                ppssTab as PpssRatingsComponent,
                agGridHelperService
              );
          }
        }
        // NjBest Price Column
        case PPSSTabName.PRICE: {
          if (state.isNjBest) {
            return getPriceColumnDefsForUS_NjBest(
              ppssTab as PpssPriceComponent,
              state,
              agGridHelperService,
              translateService
            );
          }
        }
      }
    } else if (state.siteConfig.country === 'AU') {
      switch (ppssTabName) {
        case PPSSTabName.PERFORMANCE: {
          switch ((ppssTab as PpssPerformanceComponent).productType) {
            case ConfigurationId.ETF:
              return getPerformanceColumnDefsForAU_ETF(
                ppssTab as PpssPerformanceComponent,
                state,
                agGridHelperService,
                translateService
              );
            default:
              return getDefaultPerformanceColumnDefsForAU(
                ppssTab as PpssPerformanceComponent,
                state,
                agGridHelperService,
                translateService
              );
          }
        }
        case PPSSTabName.IDENTIFIERS:
          return getDefaultIdentifierColumnDefsForIntl(
            agGridHelperService,
            translateService
          );
        case PPSSTabName.RATINGS:
          return getDefaultRatingColumnDefsForIntl(
            ppssTab as PpssRatingsComponent,
            agGridHelperService
          );
      }
    } else if (state.siteConfig.country === 'SG') {
      switch (ppssTabName) {
        case PPSSTabName.PERFORMANCE:
          return getDefaultPerformanceColumnDefsForSG(
            ppssTab as PpssPerformanceComponent,
            state,
            agGridHelperService,
            translateService
          );
        case PPSSTabName.IDENTIFIERS:
          return getDefaultIdentifierColumnDefsForIntl(
            agGridHelperService,
            translateService
          );
        case PPSSTabName.RATINGS:
          return getDefaultRatingColumnDefsForIntl(
            ppssTab as PpssRatingsComponent,
            agGridHelperService
          );
      }
    } else if (state.siteConfig.country === 'CZ') {
      switch (ppssTabName) {
        case PPSSTabName.PERFORMANCE:
          return getDefaultPerformanceColumnDefsForIntl(
            ppssTab as PpssPerformanceComponent,
            state,
            agGridHelperService,
            translateService
          );
        case PPSSTabName.IDENTIFIERS:
          return getDefaultIdentifierColumnDefsForIntl(
            agGridHelperService,
            translateService
          );
        case PPSSTabName.RATINGS:
          return getDefaultRatingColumnDefsForCZ(
            ppssTab as PpssRatingsComponent,
            agGridHelperService
          );
      }
    } else if (state.siteConfig.country === 'ES') {
      switch (ppssTabName) {
        case PPSSTabName.PERFORMANCE:
          return getDefaultPerformanceColumnDefsForIntl(
            ppssTab as PpssPerformanceComponent,
            state,
            agGridHelperService,
            translateService
          );
        case PPSSTabName.IDENTIFIERS:
          return getDefaultIdentifierColumnDefsForIntl(
            agGridHelperService,
            translateService
          );
        case PPSSTabName.RATINGS:
          return getDefaultRatingColumnDefsForES(
            ppssTab as PpssRatingsComponent,
            agGridHelperService
          );
      }
    } else if (state.siteConfig.country === 'CA') {
      switch (ppssTabName) {
        case PPSSTabName.PERFORMANCE:
          switch ((ppssTab as PpssPerformanceComponent).productType) {
            case ConfigurationId.ETF:
              return getPerformanceColumnDefsForCA_ETF(
                ppssTab as PpssPerformanceComponent,
                state,
                agGridHelperService,
                translateService
              );
            default:
              return getDefaultPerformanceColumnDefsForIntl(
                ppssTab as PpssPerformanceComponent,
                state,
                agGridHelperService,
                translateService
              );
          }

        case PPSSTabName.IDENTIFIERS:
          return getDefaultIdentifierColumnDefsForCA(
            agGridHelperService,
            translateService
          );
        case PPSSTabName.RATINGS:
          return getDefaultRatingColumnDefsForCA(
            ppssTab as PpssRatingsComponent,
            agGridHelperService
          );
      }
    } else if (state.siteConfig.country === 'PL') {
      switch (ppssTabName) {
        case PPSSTabName.PERFORMANCE:
          return getDefaultPerformanceColumnDefsForIntl(
            ppssTab as PpssPerformanceComponent,
            state,
            agGridHelperService,
            translateService
          );
        case PPSSTabName.IDENTIFIERS:
          return getDefaultIdentifierColumnDefsForIntl(
            agGridHelperService,
            translateService
          );
        case PPSSTabName.RATINGS:
          return getDefaultRatingColumnDefsForPL(
            ppssTab as PpssRatingsComponent,
            agGridHelperService
          );
      }
    } else if (state.siteConfig.country === 'BE') {
      switch (ppssTabName) {
        case PPSSTabName.PERFORMANCE:
          return getDefaultPerformanceColumnDefsForBelgium(
            ppssTab as PpssPerformanceComponent,
            state,
            agGridHelperService,
            translateService
          );
        case PPSSTabName.IDENTIFIERS:
          return getDefaultIdentifierColumnDefsForIntl(
            agGridHelperService,
            translateService
          );
        case PPSSTabName.RATINGS:
          return getDefaultRatingColumnDefsForIntl(
            ppssTab as PpssRatingsComponent,
            agGridHelperService
          );
      }
    } else if (state.siteConfig.country === 'BR') {
      switch (ppssTabName) {
        case PPSSTabName.PERFORMANCE:
          return getDefaultPerformanceColumnDefsForBR(
            ppssTab as PpssPerformanceComponent,
            state,
            agGridHelperService,
            translateService
          );
        case PPSSTabName.IDENTIFIERS:
          return getDefaultIdentifierColumnDefsForIntl(
            agGridHelperService,
            translateService
          );
        case PPSSTabName.RATINGS:
          return getDefaultRatingColumnDefsForBR(
            ppssTab as PpssRatingsComponent,
            agGridHelperService
          );
      }
    } else if (state.siteConfig.country === 'NRC') {
      // Offshore
      switch (ppssTabName) {
        case PPSSTabName.PERFORMANCE:
          return getDefaultPerformanceColumnDefsForIntl(
            ppssTab as PpssPerformanceComponent,
            state,
            agGridHelperService,
            translateService
          );
        case PPSSTabName.IDENTIFIERS:
          return getDefaultIdentifierColumnDefsForIntl(
            agGridHelperService,
            translateService
          );
        case PPSSTabName.RATINGS:
          return getDefaultRatingColumnDefsForOffshore(
            ppssTab as PpssRatingsComponent,
            agGridHelperService
          );
      }
    } else if (state.siteConfig.country === 'ZA') {
      // South Africa
      switch (ppssTabName) {
        case PPSSTabName.PERFORMANCE:
          return getDefaultPerformanceColumnDefsForZA(
            ppssTab as PpssPerformanceComponent,
            state,
            agGridHelperService,
            translateService
          );
        case PPSSTabName.IDENTIFIERS:
          return getDefaultIdentifierColumnDefsForIntl(
            agGridHelperService,
            translateService
          );
        case PPSSTabName.RATINGS:
          return getDefaultRatingColumnDefsForIntl(
            ppssTab as PpssRatingsComponent,
            agGridHelperService
          );
      }
    } else if (['DE', 'AT'].includes(state.siteConfig.country)) {
      // Germany + Austria
      switch (ppssTabName) {
        case PPSSTabName.PERFORMANCE:
          return getDefaultPerformanceColumnDefsForDE(
            ppssTab as PpssPerformanceComponent,
            state,
            agGridHelperService,
            translateService
          );
        case PPSSTabName.IDENTIFIERS:
          return getDefaultIdentifierColumnDefsForIntl(
            agGridHelperService,
            translateService
          );
        case PPSSTabName.RATINGS:
          return getDefaultRatingColumnDefsForDE(
            ppssTab as PpssRatingsComponent,
            agGridHelperService
          );
      }
    } else {
      switch (ppssTabName) {
        case PPSSTabName.PERFORMANCE:
          return getDefaultPerformanceColumnDefsForIntl(
            ppssTab as PpssPerformanceComponent,
            state,
            agGridHelperService,
            translateService
          );
        case PPSSTabName.IDENTIFIERS:
          return getDefaultIdentifierColumnDefsForIntl(
            agGridHelperService,
            translateService
          );
        case PPSSTabName.RATINGS:
          return getDefaultRatingColumnDefsForIntl(
            ppssTab as PpssRatingsComponent,
            agGridHelperService
          );
      }
    }
  }
}
